/**
 * De qué anuncio vino el visitante.
 *
 * Una campaña de catálogo en Meta no le manda ningún evento a GoAuto: el
 * visitante hace clic y aterriza en la ficha del auto. Lo único que viaja es lo
 * que la campaña puso en la URL y que Meta SUSTITUYE al momento del clic
 * (`url_tags` del creative): los ids reales de campaña, conjunto y anuncio.
 *
 * Esos parámetros están en la URL de ENTRADA y en ninguna otra. Si el visitante
 * mira tres autos antes de escribir, la URL del formulario ya no los tiene. Por
 * eso se guardan al llegar y se leen al enviar.
 *
 * ── Por qué sessionStorage y no una cookie ────────────────────────────────
 *
 * Porque no hace falta que el servidor lo vea: el lead se arma en el navegador
 * y el dato viaja en el cuerpo del envío. Una cookie se mandaría en cada
 * request de todo el sitio sin que nadie la use. Y porque la atribución es de
 * ESTA visita: que se borre al cerrar la pestaña es el comportamiento correcto,
 * no una limitación.
 *
 * ── Qué NO hace ───────────────────────────────────────────────────────────
 *
 * No decide nada. Guarda lo que vino y lo entrega tal cual. Quién es
 * determinístico, quién es inferido y qué se descarta se decide del lado del
 * admin (`src/lib/anuncios/atribucion.ts`), donde están las campañas con las
 * que cruzar. Acá sólo se evita perder la señal.
 */

const CLAVE = 'goauto_anuncio_origen_v1';

/** Los parámetros que puede traer un clic de anuncio. */
export interface AnuncioOrigen {
  /** `{{site_source_name}}` — Facebook, Instagram… */
  utm_source?: string | null;
  utm_medium?: string | null;
  /** `{{campaign.id}}` — el id de la campaña en Meta. */
  utm_campaign?: string | null;
  /** `{{adset.id}}` */
  utm_term?: string | null;
  /** `{{ad.id}}` */
  utm_content?: string | null;
  /** El id del clic. Prueba que vino de Meta; no nombra la campaña. */
  fbclid?: string | null;
  /** Dónde aterrizó. De acá sale el vehículo del lado del admin. */
  landing?: string | null;
  /** Cuándo llegó, en ISO. Sirve para auditar sin volver a Meta. */
  at?: string | null;
}

const PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid'] as const;

/** Los parámetros de anuncio que trae una URL, o `null` si no trae ninguno. */
export function origenDeUrl(href: string): AnuncioOrigen | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const encontrados: AnuncioOrigen = {};
  let alguno = false;
  for (const p of PARAMS) {
    const v = url.searchParams.get(p);
    if (v && v.trim()) {
      encontrados[p] = v.trim();
      alguno = true;
    }
  }
  if (!alguno) return null;
  // La landing va SIN el query: los parámetros ya están en sus campos y
  // repetirlos acá sólo hace más difícil leer la evidencia.
  encontrados.landing = `${url.origin}${url.pathname}`;
  return encontrados;
}

/**
 * Guarda el origen si esta URL trae uno.
 *
 * El último clic gana: si el visitante vuelve a entrar por otro anuncio, esa es
 * la visita que corresponde atribuir. Una URL sin parámetros NO borra lo que
 * había — es justamente el caso de navegar por el sitio después de llegar.
 */
export function recordarOrigen(href: string, ahora = () => new Date().toISOString()): AnuncioOrigen | null {
  const nuevo = origenDeUrl(href);
  if (!nuevo) return leerOrigen();
  const conFecha = { ...nuevo, at: ahora() };
  try {
    window.sessionStorage.setItem(CLAVE, JSON.stringify(conFecha));
  } catch {
    // Pestaña privada o almacenamiento bloqueado: se pierde la atribución de
    // esta visita y el formulario sigue funcionando. Perder un dato de
    // medición nunca puede impedir que alguien consulte por un auto.
  }
  return conFecha;
}

/** Lo guardado en esta visita, o `null`. */
export function leerOrigen(): AnuncioOrigen | null {
  try {
    const crudo = window.sessionStorage.getItem(CLAVE);
    if (!crudo) return null;
    const o = JSON.parse(crudo);
    return o && typeof o === 'object' ? (o as AnuncioOrigen) : null;
  } catch {
    return null;
  }
}

export function olvidarOrigen(): void {
  try {
    window.sessionStorage.removeItem(CLAVE);
  } catch {
    // Nada que hacer: ver `recordarOrigen`.
  }
}
